import { ArrowDown, ArrowUp, Trash2, X } from 'lucide-react';
import * as Papa from 'papaparse';
import React, { useCallback,useMemo, useRef, useState } from 'react';

import {
  delimiterToLabel,
  detectTabularFormat,
  type FormatLabel,
  formatToDelimiter,
} from '@/lib/tabularFormatDetection';
import { cn } from '@/lib/utils';

import { EditableCell, EditableHeaderCell } from './TabularEditableCell';
import { TabularTableToolbar } from './TabularTableToolbar';
import { useTableKeyboard } from './useTableKeyboard';
import { useTableSelection } from './useTableSelection';

export type TabularFormat = 'auto' | 'csv' | 'tsv' | 'pipe' | 'semicolon';

export type SortDirection = 'asc' | 'desc' | 'none';

export interface TabularTableViewProps {
  input: string;
  format: TabularFormat;
  hasHeader: boolean;
  trimWhitespace: boolean;
  editMode?: boolean;
  onCellEdit?: (rowIndex: number, colIndex: number, newValue: string) => void;
  onAddRow?: () => void;
  onAddCol?: () => void;
  onFormatChange?: (format: string) => void;
  onDeleteRow?: (rowIndex: number) => void;
  onDeleteCol?: (colIndex: number) => void;
  onHeaderEdit?: (colIndex: number, newValue: string) => void;
  onConvertFormat?: (targetDelimiter: string) => void;
  onSort?: (colIndex: number, direction: SortDirection) => void;
  onPasteCells?: (rowIndex: number, colIndex: number, values: string[][]) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}

/* ── Helpers ─────────────────────────────────────────────── */

function resolveDelimiter(format: TabularFormat, input: string): string {
  const explicit = formatToDelimiter(format);
  if (explicit) return explicit;
  const detected = detectTabularFormat(input);
  return detected?.delimiter ?? ',';
}

export function getSerializeDelimiter(format: TabularFormat, detectedDelimiter: string): string {
  if (format === 'csv') return ',';
  if (format === 'tsv') return '\t';
  if (format === 'pipe') return '|';
  if (format === 'semicolon') return ';';
  return detectedDelimiter;
}

/* ── Main component ──────────────────────────────────────── */

export const TabularTableView: React.FC<TabularTableViewProps> = ({
  input,
  format,
  hasHeader,
  trimWhitespace,
  editMode = false,
  onCellEdit,
  onAddRow,
  onAddCol,
  onFormatChange,
  onDeleteRow,
  onDeleteCol,
  onHeaderEdit,
  onConvertFormat,
  onSort,
  onPasteCells,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}) => {
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>('none');
  const tableRef = useRef<HTMLDivElement>(null);

  const parsed = useMemo(() => {
    const delimiter = resolveDelimiter(format, input || '');

    const result = Papa.parse<string[]>(input || '', {
      delimiter,
      skipEmptyLines: false,
      dynamicTyping: false,
      transform: trimWhitespace
        ? (value) => (typeof value === 'string' ? value.trim() : String(value))
        : undefined,
    });

    return { delimiter, result };
  }, [format, input, trimWhitespace]);

  const formatInfo = useMemo(() => {
    if (format !== 'auto') {
      const label = delimiterToLabel(formatToDelimiter(format) ?? ',');
      return { label, isAuto: false, confidence: 100 };
    }
    const detected = detectTabularFormat(input || '');
    if (detected) {
      return { label: detected.formatLabel, isAuto: true, confidence: detected.confidence };
    }
    return { label: delimiterToLabel(parsed.delimiter) as FormatLabel, isAuto: true, confidence: 50 };
  }, [format, input, parsed.delimiter]);

  const handleHeaderClick = useCallback((colIndex: number) => {
    let nextDir: SortDirection;
    if (sortCol !== colIndex) {
      nextDir = 'asc';
    } else if (sortDir === 'asc') {
      nextDir = 'desc';
    } else if (editMode) {
      nextDir = 'asc';
    } else {
      nextDir = 'none';
    }

    if (editMode && onSort) {
      onSort(colIndex, nextDir);
      setSortCol(nextDir === 'none' ? null : colIndex);
      setSortDir(nextDir);
    } else {
      setSortCol(nextDir === 'none' ? null : colIndex);
      setSortDir(nextDir);
    }
  }, [sortCol, sortDir, editMode, onSort]);

  const rows = (parsed.result.data ?? []) as unknown as string[][];
  const isEmpty = rows.length === 0;

  const headerRow = isEmpty
    ? (hasHeader ? ['Column 1'] : null)
    : (hasHeader ? rows[0] : null);
  const rawDataRows = isEmpty
    ? [['']]
    : (hasHeader ? rows.slice(1) : rows);
  const columnCount = isEmpty
    ? 1
    : Math.max(
        ...(rows.map((r) => (Array.isArray(r) ? r.length : 0)) as number[]),
        headerRow?.length ?? 0,
      );
  const headers = headerRow ?? Array.from({ length: columnCount }, (_, i) => `Column ${i + 1}`);

  const dataRows = useMemo(() => {
    if (editMode || sortCol === null || sortDir === 'none') return rawDataRows;

    const indexed = rawDataRows.map((row, i) => ({ row, origIdx: i }));
    indexed.sort((a, b) => {
      const aVal = String(a.row[sortCol] ?? '');
      const bVal = String(b.row[sortCol] ?? '');
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return indexed.map((item) => item.row);
  }, [rawDataRows, sortCol, sortDir, editMode]);

  const {
    selectedCell,
    selection,
    editingCell,
    selectCell,
    enterEdit,
    exitEdit,
    handleCopy,
    handlePaste,
  } = useTableSelection({
    dataRows,
    columnCount,
    editMode,
    onCellEdit,
    onPasteCells,
    focusRef: tableRef,
  });

  const handleKeyDown = useTableKeyboard({
    rowCount: dataRows.length,
    columnCount,
    selectedCell,
    editingCell,
    editMode,
    selectCell,
    enterEdit,
    exitEdit,
    onCopy: handleCopy,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
  });

  if (parsed.result.errors.length > 0) {
    const first = parsed.result.errors[0];
    return (
      <div className="p-4 text-red-500">
        <div className="font-semibold">Tabular Parse Error</div>
        <div className="text-sm mt-1">
          {first.message} (row {first.row ?? 'unknown'})
        </div>
      </div>
    );
  }

  const nonEmptyDataRows = rawDataRows.filter((r) => r.some((c) => c.trim() !== ''));
  const canDelete = editMode && (nonEmptyDataRows.length > 1 || columnCount > 1);

  return (
    <div className="p-3">
      <TabularTableToolbar
        formatInfo={formatInfo}
        format={format}
        editMode={editMode}
        isEmpty={isEmpty}
        rowCount={rawDataRows.length}
        columnCount={columnCount}
        onFormatChange={onFormatChange}
        onConvertFormat={onConvertFormat}
        onAddRow={onAddRow}
        onAddCol={onAddCol}
      />

      <div
        ref={tableRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className="overflow-auto border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-violet-500/40"
      >
        <table className="min-w-full text-sm">
          <thead className="bg-muted/30 sticky top-0">
            <tr>
              {editMode && onDeleteRow && <th className="w-8 border-b border-border" />}
              {headers.map((h, idx) => (
                <th
                  key={idx}
                  className="text-left font-medium text-muted-foreground px-3 py-2 border-b border-border whitespace-nowrap select-none cursor-pointer hover:bg-muted/50 transition-colors group"
                  onClick={() => handleHeaderClick(idx)}
                >
                  <div className="flex items-center gap-1">
                    {editMode && onHeaderEdit ? (
                      <EditableHeaderCell
                        value={String(h ?? '')}
                        colIndex={idx}
                        onHeaderEdit={onHeaderEdit}
                      />
                    ) : (
                      <span>{String(h ?? '')}</span>
                    )}
                    {sortCol === idx && sortDir === 'asc' && (
                      <ArrowUp className="h-3 w-3 text-violet-400 shrink-0" />
                    )}
                    {sortCol === idx && sortDir === 'desc' && (
                      <ArrowDown className="h-3 w-3 text-violet-400 shrink-0" />
                    )}
                    {editMode && onDeleteCol && columnCount > 1 && (
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); onDeleteCol(idx); }}
                        className="ml-auto opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity p-0.5"
                        title="Delete column"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rIdx) => (
              <tr key={rIdx} className={cn(rIdx % 2 === 0 ? 'bg-background' : 'bg-muted/10', 'group/row')}>
                {editMode && onDeleteRow && (
                  <td className="px-1 py-2 border-b border-border align-top">
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => onDeleteRow(rIdx)}
                        className="opacity-0 group-hover/row:opacity-100 text-red-400 hover:text-red-300 transition-opacity p-0.5"
                        title="Delete row"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </td>
                )}
                {Array.from({ length: columnCount }, (_, cIdx) => {
                  const isSelected = selectedCell?.row === rIdx && selectedCell?.col === cIdx;
                  const isInRange = selection
                    ? rIdx >= selection.startRow
                      && rIdx <= selection.endRow
                      && cIdx >= selection.startCol
                      && cIdx <= selection.endCol
                    : false;
                  const isEditing = editingCell?.row === rIdx && editingCell?.col === cIdx;
                  return (
                    <td
                      key={cIdx}
                      className={cn(
                        'px-3 py-2 border-b border-border align-top whitespace-nowrap relative',
                        isInRange && 'bg-violet-500/10',
                        isSelected && 'ring-1 ring-violet-400/70 ring-inset',
                      )}
                      onClick={(event) => selectCell({ row: rIdx, col: cIdx }, { extend: event.shiftKey })}
                    >
                      {editMode && onCellEdit ? (
                        <EditableCell
                          value={String((row as string[])?.[cIdx] ?? '')}
                          rowIndex={rIdx}
                          colIndex={cIdx}
                          isEditing={isEditing}
                          onCellEdit={onCellEdit}
                          onRequestEdit={() => enterEdit({ row: rIdx, col: cIdx })}
                          onFinishEdit={exitEdit}
                        />
                      ) : (
                        String((row as string[])?.[cIdx] ?? '') || '\u00A0'
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
